import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "@effect/platform";
import { ParseResult } from "effect";
import * as S from "effect/Schema";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { CalendarDateFromString, InstantFromString } from "./temporal.js";
import { DueDateIntervalSchema, MeasureCategorySchema, MeasurePrioritySchema, RecurrenceSchema, } from "./column-schemas.js";
import { DraftRecipient } from "@pima/contracts/measure-recipients";
export { DraftRecipient } from "@pima/contracts/measure-recipients";
export const TrainingAcknowledgement = S.Struct({
    confirmed: S.Boolean,
    confirmed_by: S.NullOr(S.UUID),
    confirmed_by_name: S.NullOr(S.String),
    confirmed_at: S.NullOr(S.String),
});
export const MeasureRow = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    title: S.String,
    title_t_en: S.NullOr(S.String),
    description: S.NullOr(S.String),
    description_t_en: S.NullOr(S.String),
    type: S.Literal("document", "online_course", "measure"),
    status: S.String,
    category: S.NullOr(MeasureCategorySchema),
    priority: S.NullOr(MeasurePrioritySchema),
    due_date: S.NullOr(CalendarDateFromString),
    due_date_interval: S.NullOr(DueDateIntervalSchema),
    recurring: S.Boolean,
    recurrence: S.NullOr(RecurrenceSchema),
    risk_impact: S.Boolean,
    risk_score: S.NullOr(S.Number),
    auto_reminder: S.Boolean,
    completed_on: S.NullOr(S.String),
    date_created: S.NullOr(S.String),
    next_occurrence: S.NullOr(S.String),
    location_ids: S.Array(S.String),
    document_id: S.NullOr(S.UUID),
    document_title: S.NullOr(S.String),
    document_filename_download: S.NullOr(S.String),
    document_type: S.NullOr(S.String),
    online_course_id: S.NullOr(S.UUID),
    online_course_title: S.NullOr(S.String),
    online_course_language: S.NullOr(S.String),
    attachment_title: S.NullOr(S.String),
    attachment_filename_download: S.NullOr(S.String),
    assignees_ids: S.Array(S.UUID),
    total_assignees: S.Int,
    total_completed: S.Int,
    total_overdue: S.Int,
    /** The risk instance a draft measure was minted from — `null` for every non-draft-originated measure. */
    risk_instance_id: S.NullOr(S.UUID),
    /** The share targets the draft-acceptance notice goes to once accepted. */
    draft_recipients: S.Array(DraftRecipient),
    draft_accepted_on: S.NullOr(S.String),
    draft_rejected_on: S.NullOr(S.String),
    draft_rejection_reason: S.NullOr(S.String),
    /** The measure template a draft was generated from, if any. */
    source_measure_template_id: S.NullOr(S.UUID),
    metadata: S.Unknown,
    training_acknowledgement: S.NullOr(TrainingAcknowledgement),
    /** Whether this caller may change this measure. */
    can_change: S.Boolean,
});
/** Schema boundary for typed write results, keeping Temporal inside the domain. */
export const MeasureWriteRow = S.Struct({
    ...MeasureRow.fields,
    due_date: S.NullOr(CalendarDateFromString),
    date_created: S.NullOr(InstantFromString),
    next_occurrence: S.NullOr(InstantFromString),
    draft_accepted_on: S.NullOr(InstantFromString),
    draft_rejected_on: S.NullOr(InstantFromString),
    training_acknowledgement: S.NullOr(S.Struct({
        ...TrainingAcknowledgement.fields,
        confirmed_at: S.NullOr(InstantFromString),
    })),
});
export const MeasureStatusGroup = S.Literal("active", "completed", "draft");
const UuidCsvList = S.transform(S.String, S.Array(S.UUID), {
    strict: true,
    decode: (s) => s
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    encode: (ids) => ids.join(","),
});
export const ListMeasuresParams = S.extend(PagingParams, S.Struct({
    client_id: S.UUID,
    /** Omitted ⇒ every non-draft status — the asset-scoped "Assigned" tab's shape. */
    status_group: S.optional(MeasureStatusGroup),
    category: S.optional(MeasureCategorySchema),
    /**
     * A draft-measure review lists all measures minted from any instance in
     * a risk's translation group, not one instance id — the caller resolves
     * the group's member ids and passes every one of them.
     */
    risk_instance_ids: S.optional(UuidCsvList),
    /**
     * Scopes the list to the measures attached to one document/online-course
     * translation group — the asset-detail "Assigned" tab's row source, one
     * row per measure instead of one per assignee. Both fields travel
     * together; omitted, the list is unscoped by asset as today.
     */
    asset_type: S.optional(S.Literal("document", "online_course")),
    asset_translation_group_id: S.optional(S.NumberFromString.pipe(S.int())),
}));
export const ListMeasures = HttpApiEndpoint.get("listMeasures", "/measures")
    .setUrlParams(ListMeasuresParams)
    .addSuccess(pagedResponse(MeasureRow))
    .addError(Forbidden, { status: 403 });
/** The measure id on a reminder request doesn't exist. */
export const MeasureNotFound = S.Struct({ _tag: S.Literal("MeasureNotFound") });
const Queued = S.Struct({ queued: S.Literal(true) });
export const SendMeasureReminderPayload = S.Struct({
    measure_id: S.UUID,
});
/**
 * Re-sends the reminder for every eligible assignment of one measure —
 * `auto_reminder: false` targets only overdue assignees, `true` (the
 * default) targets every non-terminal one, matching
 * `GET /assignments/measures/:id/reminder` today.
 */
export const SendMeasureReminder = HttpApiEndpoint.post("sendMeasureReminder", "/measures/reminder")
    .setPayload(SendMeasureReminderPayload)
    .addSuccess(S.Struct({ data: Queued }))
    .addError(MeasureNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const SendEmployeeReminderPayload = S.Struct({
    user_id: S.UUID,
    client_id: S.UUID,
});
export const EmployeeReminderOutcome = S.Struct({
    outcome: S.Literal("invited", "reminded", "no_pending_assignments"),
});
/** A reminder was already sent to this employee within the last 24 hours. */
export const ReminderCooldown = S.Struct({
    _tag: S.Literal("ReminderCooldown"),
});
/** The user id doesn't belong to the given client. */
export const EmployeeNotInClient = S.Struct({
    _tag: S.Literal("EmployeeNotInClient"),
});
/**
 * Re-invites an invited employee, or reminds an active one of every
 * non-complete assignment in the client — matching
 * `POST /assignments/reminder/employee` today.
 */
export const SendEmployeeReminder = HttpApiEndpoint.post("sendEmployeeReminder", "/measures/reminder/employee")
    .setPayload(SendEmployeeReminderPayload)
    .addSuccess(S.Struct({ data: EmployeeReminderOutcome }))
    .addError(EmployeeNotInClient, { status: 404 })
    .addError(ReminderCooldown, { status: 429 })
    .addError(Forbidden, { status: 403 });
/** The `type` filter on `exportMeasuresCsv` — omitted or empty means every type. */
export const MeasureExportType = S.Literal("document", "online_course", "measure");
/**
 * A comma-joined list of {@link MeasureExportType} on the wire — url params
 * on this contract must be encodeable to a single string per field, so a
 * genuine array (as `type` reads once decoded) can't ride a query param
 * directly.
 */
const MeasureExportTypeList = S.transformOrFail(S.String, S.Array(MeasureExportType), {
    strict: true,
    decode: (s, _, ast) => {
        const tokens = s
            .split(",")
            .map((t) => t.trim())
            .filter((t) => t.length > 0);
        const isValid = (t) => t === "document" || t === "online_course" || t === "measure";
        return tokens.every(isValid)
            ? ParseResult.succeed(tokens)
            : ParseResult.fail(new ParseResult.Type(ast, s, `invalid measure export type in "${s}"`));
    },
    encode: (types) => ParseResult.succeed(types.join(",")),
});
export const ExportMeasuresCsvParams = S.Struct({
    client_id: S.UUID,
    type: S.optional(MeasureExportTypeList),
    created_from: S.optional(CalendarDateFromString),
    created_to: S.optional(CalendarDateFromString),
});
/**
 * The measure-center CSV export sheet (`GET /items/vw_measures?export=csv`
 * today) — raw `text/csv` bytes, not a JSON envelope, matching the
 * cross-cutting rule for export/download endpoints. Draft and rejected
 * measures are always excluded server-side; there is no caller-supplied
 * override.
 */
export const ExportMeasuresCsv = HttpApiEndpoint.get("exportMeasuresCsv", "/measures/export")
    .setUrlParams(ExportMeasuresCsvParams)
    .addSuccess(HttpApiSchema.Uint8Array({ contentType: "text/csv; charset=utf-8" }))
    .addError(Forbidden, { status: 403 });
export const GetMeasureParams = S.Struct({ id: S.UUID });
export const GetMeasure = HttpApiEndpoint.get("getMeasure", "/measures/get")
    .setUrlParams(GetMeasureParams)
    .addSuccess(S.Struct({ data: MeasureRow }))
    .addError(MeasureNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * `vw_measure_risk_lineage` — a measure minted from a risk instance's own
 * "create measure" action carries the originating instance's id/translation
 * group/title, for the draft-measure-view modal's traceability line. A
 * measure with no lineage row (never created from a risk) is `data: null`,
 * not `MeasureNotFound` — the measure itself may well exist.
 */
export const MeasureRiskLineageRow = S.Struct({
    measure_id: S.UUID,
    client_id: S.UUID,
    risk_instance_id: S.UUID,
    risk_translation_group_id: S.NullOr(S.Int),
    risk_instance_title: S.NullOr(S.String),
});
export const GetMeasureRiskLineageParams = S.Struct({ measure_id: S.UUID });
export const GetMeasureRiskLineage = HttpApiEndpoint.get("getMeasureRiskLineage", "/measures/risk-lineage")
    .setUrlParams(GetMeasureRiskLineageParams)
    .addSuccess(S.Struct({ data: S.NullOr(MeasureRiskLineageRow) }))
    .addError(Forbidden, { status: 403 });
/**
 * Fields the dashboard measure-assign modal writes on create/edit —
 * `vw_measures`'s computed/joined columns (titles, counts, assignee ids)
 * are never part of a write payload, only the base `measures` row.
 */
const MeasureWritableFields = S.Struct({
    title: S.optional(S.NonEmptyString),
    title_t_en: S.optional(S.NullOr(S.String)),
    description: S.optional(S.NullOr(S.String)),
    description_t_en: S.optional(S.NullOr(S.String)),
    category: S.optional(MeasureCategorySchema),
    priority: S.optional(MeasurePrioritySchema),
    due_date: S.optional(S.NullOr(CalendarDateFromString)),
    due_date_interval: S.optional(S.NullOr(DueDateIntervalSchema)),
    recurring: S.optional(S.Boolean),
    recurrence: S.optional(S.NullOr(RecurrenceSchema)),
    risk_impact: S.optional(S.Boolean),
    auto_reminder: S.optional(S.Boolean),
    document_id: S.optional(S.NullOr(S.UUID)),
    online_course_id: S.optional(S.NullOr(S.UUID)),
    attachment: S.optional(S.NullOr(S.String)),
    location_ids: S.optional(S.Array(S.String)),
    draft_recipients: S.optional(S.Array(DraftRecipient)),
    status: S.optional(S.Literal("in_progress", "inactive")),
});
export const CreateMeasurePayload = S.extend(S.Struct({
    client_id: S.UUID,
    type: S.Literal("document", "online_course", "measure"),
    title: S.NonEmptyString,
    category: MeasureCategorySchema,
}), S.Struct({
    /**
     * Chosen by the client and kept for the life of the queued create. A replay
     * with the same `(client_id, client_ref)` returns the measure the first
     * attempt committed and writes nothing.
     */
    client_ref: S.optional(S.UUID),
    title_t_en: S.optional(S.NullOr(S.String)),
    description: S.optional(S.NullOr(S.String)),
    description_t_en: S.optional(S.NullOr(S.String)),
    priority: S.optional(MeasurePrioritySchema),
    due_date: S.optional(S.NullOr(CalendarDateFromString)),
    due_date_interval: S.optional(S.NullOr(DueDateIntervalSchema)),
    recurring: S.optional(S.Boolean),
    recurrence: S.optional(S.NullOr(RecurrenceSchema)),
    risk_impact: S.optional(S.Boolean),
    auto_reminder: S.optional(S.Boolean),
    document_id: S.optional(S.NullOr(S.UUID)),
    online_course_id: S.optional(S.NullOr(S.UUID)),
    attachment: S.optional(S.NullOr(S.String)),
    location_ids: S.optional(S.Array(S.String)),
    draft_recipients: S.optional(S.Array(DraftRecipient)),
    /** Set only by the risk-instance draft-measures flow; immutable once created. */
    risk_instance_id: S.optional(S.NullOr(S.UUID)),
    source_measure_template_id: S.optional(S.NullOr(S.UUID)),
    /** Omitted ⇒ `not_started` (the dashboard assign-modal's create path); a risk-instance draft measure creates directly into `draft`. */
    status: S.optional(S.Literal("not_started", "draft")),
    /** Optional assignment-time acknowledgement; identity and time are server-owned. */
    training_acknowledged: S.optional(S.Boolean),
}));
/**
 * Own-client-scoped measure create — status always starts `not_started`
 * (the same value `measure-assign-modal`'s create path hardcodes) and
 * `created_by` is always the caller, never client-supplied.
 */
export const CreateMeasure = HttpApiEndpoint.post("createMeasure", "/measures/create")
    .setPayload(CreateMeasurePayload)
    .addSuccess(S.Struct({ data: MeasureRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateMeasurePayload = S.extend(S.Struct({ id: S.UUID }), MeasureWritableFields);
export const UpdateMeasure = HttpApiEndpoint.post("updateMeasure", "/measures/update")
    .setPayload(UpdateMeasurePayload)
    .addSuccess(S.Struct({ data: MeasureRow }))
    .addError(MeasureNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/** `deleteMeasure` called on a measure whose status isn't `not_started` — matches the measure-assign modal's own delete guard. */
export const MeasureNotDeletable = S.Struct({
    _tag: S.Literal("MeasureNotDeletable"),
});
export const DeleteMeasurePayload = S.Struct({ id: S.UUID });
export const DeleteMeasure = HttpApiEndpoint.post("deleteMeasure", "/measures/delete")
    .setPayload(DeleteMeasurePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(MeasureNotFound, { status: 404 })
    .addError(MeasureNotDeletable, { status: 409 })
    .addError(Forbidden, { status: 403 });
export const DraftMeasureIncomplete = S.Struct({
    _tag: S.Literal("DraftMeasureIncomplete"),
});
export const AcceptDraftMeasurePayload = S.Struct({ id: S.UUID });
/**
 * Marks a draft accepted (`draft_accepted_on`) without changing its
 * `status` — the draft becomes an active assignment only when its
 * originating risk instance is itself accepted, a separate write this
 * intent never performs.
 */
export const AcceptDraftMeasure = HttpApiEndpoint.post("acceptDraftMeasure", "/measures/draft/accept")
    .setPayload(AcceptDraftMeasurePayload)
    .addSuccess(S.Struct({ data: MeasureRow }))
    .addError(MeasureNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 })
    .addError(DraftMeasureIncomplete, { status: 400 });
export const RejectDraftMeasurePayload = S.Struct({
    id: S.UUID,
    rejection_reason: S.NonEmptyString,
});
export const RejectDraftMeasure = HttpApiEndpoint.post("rejectDraftMeasure", "/measures/draft/reject")
    .setPayload(RejectDraftMeasurePayload)
    .addSuccess(S.Struct({ data: MeasureRow }))
    .addError(MeasureNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const MeasureAssignmentNotFound = S.Struct({
    _tag: S.Literal("MeasureAssignmentNotFound"),
});
export const UpdateMyMeasureAssignmentPayload = S.Struct({
    id: S.UUID,
    status: S.Literal("in_progress", "complete"),
});
export const MeasureAssignmentStatusResult = S.Struct({
    id: S.UUID,
    status: S.Literal("in_progress", "complete"),
    completed_at: S.NullOr(S.String),
});
export const UpdateMyMeasureAssignment = HttpApiEndpoint.post("updateMyMeasureAssignment", "/measures/assignment/update")
    .setPayload(UpdateMyMeasureAssignmentPayload)
    .addSuccess(S.Struct({ data: MeasureAssignmentStatusResult }))
    .addError(MeasureAssignmentNotFound, { status: 404 });
export const measuresGroup = HttpApiGroup.make("measures")
    .add(ListMeasures)
    .add(GetMeasure)
    .add(GetMeasureRiskLineage)
    .add(CreateMeasure)
    .add(UpdateMeasure)
    .add(DeleteMeasure)
    .add(AcceptDraftMeasure)
    .add(RejectDraftMeasure)
    .add(UpdateMyMeasureAssignment)
    .add(SendMeasureReminder)
    .add(SendEmployeeReminder)
    .add(ExportMeasuresCsv)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=measures.js.map